import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireSuperAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  requireSuperAdmin = false,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    const redirectPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/auth?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  if (requireSuperAdmin && !user.isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (allowedRoles) {
    const role = user.role;

    if (!role) {
      return <Navigate to="/auth?missingRole=1" replace />;
    }

    if (!allowedRoles.includes(role)) {
      const fallback = role === 'student' ? '/student/dashboard' : '/unauthorized';
      return <Navigate to={fallback} replace />;
    }
  }

  return <>{children}</>;
}
